import React, { useEffect, useRef, useState } from 'react';
import { setBlogFilter, useBlogFilter } from './urlFilterStore';
import { BlogOverviewFilterColumn, FilterNavigationBlogProps } from './types';

/**
 * The blog overview's filter bar (ported from regulator-marine's filterNavigationBlog block).
 *
 * Category buttons pick one category and clear any author/type selection. The subject matter panel
 * stages author and type checkboxes until "View Selections" applies them (which also resets the
 * category to All). Closing the panel with its toggle, or with Escape, clears the applied author/type
 * selection, as regulator's toggle did.
 */

type Selection = { authors: string[]; types: string[] };

type PanelState =
    | { status: 'closed' }
    | { status: 'open'; pending: Selection };

const PANEL_ID = 'blog-filter-subject-matter';

export default function FilterNavigationBlog({ subjectMatterButtonText, filterButtons, columns }: FilterNavigationBlogProps) {
    const filter = useBlogFilter();
    const [panel, setPanel] = useState<PanelState>({ status: 'closed' });
    const toggleRef = useRef<HTMLButtonElement>(null);

    const subjectActive = filter.authors.length > 0 || filter.types.length > 0;
    const hasColumns = columns.length > 0;

    const closeAndClear = () => {
        setPanel({ status: 'closed' });
        setBlogFilter({ category: filter.category, authors: [], types: [] });
        toggleRef.current?.focus();
    };

    const toggle = () => {
        if (panel.status === 'open') {
            closeAndClear();
            return;
        }
        setPanel({ status: 'open', pending: { authors: filter.authors, types: filter.types } });
    };

    const apply = () => {
        if (panel.status !== 'open') {
            return;
        }
        setBlogFilter({ category: '', authors: panel.pending.authors, types: panel.pending.types });
        setPanel({ status: 'closed' });
        toggleRef.current?.focus();
    };

    const pickCategory = (categoryKey: string) => {
        setBlogFilter({ category: categoryKey, authors: [], types: [] });
    };

    return (
        <div className={`blog-filter__bar${panel.status === 'open' ? ' is-open' : ''}`}>
            {panel.status === 'open' && (
                <SubjectMatterPanel
                    columns={columns}
                    pending={panel.pending}
                    onChange={pending => setPanel({ status: 'open', pending })}
                    onApply={apply}
                    onEscape={closeAndClear}
                />
            )}
            <div className="blog-filter__main">
                {hasColumns && (
                    <button
                        ref={toggleRef}
                        type="button"
                        className={`blog-filter__button blog-filter__button--subject${panel.status === 'open' ? ' is-open' : ''}${subjectActive ? ' is-active' : ''}`}
                        aria-expanded={panel.status === 'open'}
                        aria-controls={PANEL_ID}
                        onClick={toggle}>
                        <span className={`blog-filter__toggle-icon fa ${panel.status === 'open' ? 'fa-times' : 'fa-bars'}`} aria-hidden="true"></span>
                        {subjectMatterButtonText}
                    </button>
                )}
                {panel.status === 'closed' && (
                    <div className="blog-filter__categories" role="group" aria-label="Filter by category">
                        <CategoryButton label="All" icon={null} pressed={filter.category === '' && !subjectActive} onClick={() => pickCategory('')} />
                        {filterButtons.map(button => (
                            <CategoryButton
                                key={button.categoryKey}
                                label={button.label}
                                icon={button.icon}
                                pressed={filter.category === button.categoryKey}
                                onClick={() => pickCategory(button.categoryKey)} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

type CategoryButtonProps = {
    label: string;
    icon: string | null;
    pressed: boolean;
    onClick: () => void;
};

function CategoryButton({ label, icon, pressed, onClick }: CategoryButtonProps) {
    return (
        <button type="button" className={`blog-filter__button${pressed ? ' is-active' : ''}`} aria-pressed={pressed} onClick={onClick}>
            {/* The server only passes a plain Font Awesome name here (BlogOverviewService.IconClass). */}
            {icon && <span className={`blog-filter__button-icon fa ${icon}`} aria-hidden="true"></span>}
            {label}
        </button>
    );
}

type SubjectMatterPanelProps = {
    columns: BlogOverviewFilterColumn[];
    pending: Selection;
    onChange: (pending: Selection) => void;
    onApply: () => void;
    onEscape: () => void;
};

function SubjectMatterPanel({ columns, pending, onChange, onApply, onEscape }: SubjectMatterPanelProps) {
    const panelRef = useRef<HTMLDivElement>(null);

    // Focus moves into the panel when it opens, so keyboard users land on the first checkbox.
    useEffect(() => {
        panelRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    }, []);

    const onKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            onEscape();
        }
    };

    const toggleKey = (param: BlogOverviewFilterColumn['param'], key: string) => {
        const list = param === 'author' ? pending.authors : pending.types;
        const next = list.indexOf(key) >= 0 ? list.filter(k => k !== key) : list.concat(key);
        onChange(param === 'author' ? { authors: next, types: pending.types } : { authors: pending.authors, types: next });
    };

    return (
        <div id={PANEL_ID} ref={panelRef} className="blog-filter__panel" onKeyDown={onKeyDown}>
            <div className="blog-filter__columns">
                {columns.map((column, index) => (
                    <fieldset key={`${column.param}-${index}`} className="blog-filter__column">
                        <legend className="blog-filter__column-title">{column.title}</legend>
                        <ul className="blog-filter__options">
                            {column.options.map(option => {
                                const list = column.param === 'author' ? pending.authors : pending.types;
                                const id = `blog-filter-${column.param}-${option.key}`;
                                return (
                                    <li key={option.key} className="blog-filter__option">
                                        <input
                                            id={id}
                                            className="blog-filter__checkbox"
                                            type="checkbox"
                                            value={option.key}
                                            checked={list.indexOf(option.key) >= 0}
                                            onChange={() => toggleKey(column.param, option.key)} />
                                        <label htmlFor={id} className="blog-filter__option-label">{option.name}</label>
                                    </li>
                                );
                            })}
                        </ul>
                    </fieldset>
                ))}
            </div>
            <button type="button" className="blog-filter__apply" onClick={onApply}>View Selections</button>
        </div>
    );
}
